import { fetchApiGraphQL } from '@/utils/fetcher';
import {
  getUserAffiliationsMutation,
  type GetUserAffiliationsResult,
  type UserAffiliation,
  removeUserAffiliationMutation,
  type RemoveUserAffiliationResult
} from "./affiliation.query";

export type Affiliation = {
  affiliationId?: string;
  affiliationType: string;
  fields: Record<string, string>;
};

export type AffiliationTypeConfig = {
  label: string;
  limit: number;
  ownCpr: boolean;
  fields: string[];
};

export const affiliationTypes: Record<string, AffiliationTypeConfig> = {
  PkaMedlem: { label: 'Medlem af PKA', limit: 1, ownCpr: true, fields: [] },
  PkaTjenestemand: { label: 'Tjenestemand (DSR-medlem)', limit: 1, ownCpr: false, fields: [] },
  PkaStuderende: {
    label: 'Studerende/Elev',
    limit: 1,
    ownCpr: false,
    fields: ['studentProgram', 'studentEndDate', 'studyCheckDocument', 'proofOfPaidUnionMembership'],
  },
  PkaDsrMedlemUdenPka: { label: 'DSR-medlem (uden PKA medlemskab)', limit: 1, ownCpr: false, fields: [] },
  PkaBarnAfMedlem: { label: 'Barn af PKA-medlem', limit: 2, ownCpr: true, fields: ['memberCprNumber'] },
};

export function affiliationLabel(affiliationType: string): string {
  return affiliationTypes[affiliationType]?.label ?? affiliationType;
}

function toAffiliation(serverAffiliation: UserAffiliation): Affiliation {
  return {
    affiliationId: serverAffiliation.affiliationId,
    affiliationType: serverAffiliation.affiliationType,
    fields: Object.fromEntries(serverAffiliation.fields.map(f => [f.key, f.value])),
  };
}

export async function getAffiliations(): Promise<Affiliation[]> {
  const result = await fetchApiGraphQL<GetUserAffiliationsResult>(
    getUserAffiliationsMutation,
    {}
  );

  return (result?.data?.getUserAffiliations ?? []).map(toAffiliation);
}

export async function checkAffiliationLimit(affiliationType: string, editingId?: string): Promise<string | null> {
  const affiliations = await getAffiliations();

  const limit = affiliationTypes[affiliationType]?.limit ?? 1;
  const count = affiliations.filter(
    a => a.affiliationType === affiliationType && a.affiliationId !== editingId
  ).length;

  return count >= limit ? 'affiliation-duplicate' : null;
}

export async function removeAffiliation(affiliationId: string): Promise<string | null> {
  try {
    const result = await fetchApiGraphQL<RemoveUserAffiliationResult>(
      removeUserAffiliationMutation,
      { affiliationId }
    );

    return result?.data?.removeUserAffiliation ? null : 'affiliation-failed';
  } catch {
    return 'affiliation-failed';
  }
}
